import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import type { TConversation } from "../../types/agent";

import { BinIcon } from "../../assets/icons/binIcon";
import { ErrorIcon } from "../../assets/icons/errorIcon";
import convSearchIcon from "../../assets/svg/agent-conv-search.svg";
import newChatIcon from "../../assets/svg/agent-new-chat.svg";
import sidebarIcon from "../../assets/svg/agent-sidebar.svg";
import { setSelectedConversation } from "../../redux/agent/agent.slice";
import { deleteConversation } from "../../redux/agent/agent.thunk";
import {
	useAppDispatch,
	useAppSelector,
} from "../../redux/store";
import { DeleteConfirmationModal } from "../../shared/DeleteConfirmationModal";
import { LoaderWithText } from "../../shared/Loader";
import { groupConversationsByDate } from "../../utils/groupConversationsByDate";
import { toTenantPath } from "../../utils/tenantUtils";

import SidebarSearch from "./SidebarSearch";
import UserAvatar from "./UserAvatar";

type TSidebarProps = {
	conversations: Array<TConversation>;
	loading: boolean;
	isCollapsed: boolean;
	toggleSidebar: () => void;
	selectedConversation: TConversation | null;
	error: string | null;
	onRetryConversations: () => void;
	readOnly?: boolean;
};

const EveryoneScopeIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 16 16"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		aria-hidden="true"
		focusable="false"
	>
		<path
			d="M6.2 7.2C7.35 7.2 8.28 6.27 8.28 5.12C8.28 3.97 7.35 3.04 6.2 3.04C5.05 3.04 4.12 3.97 4.12 5.12C4.12 6.27 5.05 7.2 6.2 7.2Z"
			stroke="currentColor"
			strokeWidth="1.35"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<path
			d="M9.8 6.86C10.73 6.86 11.48 6.11 11.48 5.18C11.48 4.25 10.73 3.5 9.8 3.5"
			stroke="currentColor"
			strokeWidth="1.35"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<path
			d="M2.8 12.36C2.8 10.82 4.32 9.58 6.2 9.58C8.08 9.58 9.6 10.82 9.6 12.36"
			stroke="currentColor"
			strokeWidth="1.35"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<path
			d="M10.18 9.84C11.52 10.16 12.48 11.16 12.48 12.36"
			stroke="currentColor"
			strokeWidth="1.35"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const Sidebar: React.FC<TSidebarProps> = ({
	conversations,
	loading,
	isCollapsed,
	toggleSidebar,
	selectedConversation,
	error,
	onRetryConversations,
	readOnly = false,
}) => {
	const [searchQuery, setSearchQuery] = useState("");
	const [isSearchVisible, setIsSearchVisible] =
		useState(false);
	const [isScopeMenuOpen, setIsScopeMenuOpen] =
		useState(false);
	const [
		conversationPendingDeletion,
		setConversationPendingDeletion,
	] = useState<TConversation | null>(null);
	const [deletingConversationId, setDeletingConversationId] =
		useState<string | null>(null);
	const scopeDropdownRef = useRef<HTMLDivElement | null>(
		null
	);
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const { user, usernameFromToken } = useAppSelector(
		(state) => state.user
	);
	const { activeTenant } = useAppSelector(
		(state) => state.tenant
	);
	const currentUserName =
		user?.name || usernameFromToken || "You";

	const [showAllConversations, setShowAllConversations] =
		useState(false);

	useEffect(() => {
		if (!isScopeMenuOpen) {
			return;
		}

		const handlePointerDown = (
			event: MouseEvent | TouchEvent
		) => {
			if (
				scopeDropdownRef.current?.contains(
					event.target as Node
				)
			) {
				return;
			}

			setIsScopeMenuOpen(false);
		};

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				setIsScopeMenuOpen(false);
			}
		};

		document.addEventListener(
			"mousedown",
			handlePointerDown
		);
		document.addEventListener(
			"touchstart",
			handlePointerDown
		);
		document.addEventListener("keydown", handleKeyDown);

		return () => {
			document.removeEventListener(
				"mousedown",
				handlePointerDown
			);
			document.removeEventListener(
				"touchstart",
				handlePointerDown
			);
			document.removeEventListener(
				"keydown",
				handleKeyDown
			);
		};
	}, [isScopeMenuOpen]);

	const handleNewChat = () => {
		if (readOnly) {
			return;
		}
		dispatch(setSelectedConversation(null));
		navigate(toTenantPath("/agent", activeTenant));
	};

	const handleSearch = (query: string) => {
		setSearchQuery(query.toLowerCase());
	};

	const toggleSearch = () => {
		setIsSearchVisible(!isSearchVisible);
		if (isSearchVisible) {
			setSearchQuery("");
		}
	};

	const handleOpenDeleteConversation = (
		event: React.MouseEvent<HTMLButtonElement>,
		conversation: TConversation
	) => {
		event.preventDefault();
		event.stopPropagation();

		if (readOnly || !conversation.is_owner) {
			return;
		}

		setConversationPendingDeletion(conversation);
	};

	const handleCloseDeleteConversation = () => {
		if (deletingConversationId !== null) {
			return;
		}

		setConversationPendingDeletion(null);
	};

	const handleConfirmDeleteConversation = async () => {
		if (!conversationPendingDeletion) {
			return;
		}

		const conversationId =
			conversationPendingDeletion.conv_id;
		setDeletingConversationId(conversationId);

		const result = await dispatch(
			deleteConversation(conversationId)
		);

		setDeletingConversationId(null);

		if (deleteConversation.fulfilled.match(result)) {
			toast.success("Conversation deleted.");
			setConversationPendingDeletion(null);

			if (
				selectedConversation?.conv_id === conversationId
			) {
				navigate(toTenantPath("/agent", activeTenant));
			}

			return;
		}

		const errorMessage =
			typeof result.payload === "string"
				? result.payload
				: "Paul conversation could not be deleted.";
		toast.error(errorMessage);
	};

	if (loading) {
		return (
			<div className="agent-sidebar-container">
				<LoaderWithText
					withText={true}
					text="loading conversations..."
					loaderWidth="30"
				/>
			</div>
		);
	}

	if (error) {
		return (
			<div className="agent-sidebar-container">
				<div className="sidebar-error">
					<ErrorIcon />
					<p>Error loading conversations.</p>
					<span>{error}</span>
					<button
						type="button"
						className="conversation-load-retry-button"
						onClick={onRetryConversations}
					>
						Retry
					</button>
				</div>
			</div>
		);
	}

	const matchingConversations = conversations.filter(
		(convo) =>
			convo.title.toLowerCase().includes(searchQuery)
	);
	const ownerMatchingConversations =
		matchingConversations.filter((convo) => convo.is_owner);
	const ownerConversationCount =
		ownerMatchingConversations.length;
	const allConversationCount = matchingConversations.length;
	const selectedScopeLabel = showAllConversations
		? "Everyone's conversations"
		: `${currentUserName}'s conversations`;
	const selectedScopeCount = showAllConversations
		? allConversationCount
		: ownerConversationCount;
	const filteredConversations =
		matchingConversations.filter(
			(convo) => showAllConversations || convo.is_owner
		);

	const groupedConversations = groupConversationsByDate(
		filteredConversations
	);
	const hasConversations = filteredConversations.length > 0;
	const hasSharedMatches = matchingConversations.some(
		(convo) => !convo.is_owner
	);
	const shouldPromptForAll =
		!showAllConversations &&
		ownerMatchingConversations.length === 0 &&
		hasSharedMatches;
	const emptyStateMessage =
		searchQuery.trim().length > 0
			? "No conversations match your search."
			: shouldPromptForAll
				? 'You have no conversations in this tenant. Switch to "All" to see shared conversations.'
				: "No conversations found. Click the new chat icon to start a conversation.";

	return (
		<div
			className={`agent-sidebar-container ${
				isCollapsed ? "collapsed" : ""
			}`}
		>
			<div className="agent-sidebar-header">
				<button
					type="button"
					className="agent-sidebar-header-button"
					aria-label="Toggle sidebar"
					title="Toggle sidebar"
					onClick={toggleSidebar}
				>
					<img
						src={sidebarIcon}
						alt=""
						aria-hidden="true"
					/>
				</button>
				{!isCollapsed && (
					<div className="agent-sidebar-action-group">
						<div className="agent-sidebar-header-icons">
							<button
								type="button"
								className="agent-sidebar-header-button"
								aria-label={
									isSearchVisible
										? "Close conversation search"
										: "Search conversations"
								}
								title={
									isSearchVisible
										? "Close search"
										: "Search conversations"
								}
								onClick={toggleSearch}
							>
								<img
									src={convSearchIcon}
									alt=""
									aria-hidden="true"
								/>
							</button>
							{readOnly ? null : (
								<button
									type="button"
									className="agent-sidebar-header-button"
									aria-label="New chat"
									title="New chat"
									onClick={handleNewChat}
								>
									<img
										src={newChatIcon}
										alt=""
										aria-hidden="true"
									/>
								</button>
							)}
						</div>
						<div
							className="agent-sidebar-scope-select"
							ref={scopeDropdownRef}
						>
							<button
								type="button"
								className="agent-sidebar-header-button agent-sidebar-scope-trigger"
								aria-haspopup="listbox"
								aria-expanded={isScopeMenuOpen}
								aria-label={`Conversation scope: ${selectedScopeLabel}, ${selectedScopeCount} conversations`}
								title={`${selectedScopeLabel} (${selectedScopeCount})`}
								onClick={() => {
									setIsScopeMenuOpen((isOpen) => !isOpen);
								}}
							>
								<span className="agent-sidebar-scope-avatar">
									{showAllConversations ? (
										<EveryoneScopeIcon />
									) : (
										<UserAvatar
											userName={currentUserName}
											size={22}
											fontSize={9}
										/>
									)}
								</span>
							</button>
							{isScopeMenuOpen && (
								<div
									className="agent-sidebar-scope-menu"
									role="listbox"
									aria-label="Conversation scope options"
								>
									<button
										type="button"
										role="option"
										aria-selected={!showAllConversations}
										className={`agent-sidebar-scope-menu-option ${
											!showAllConversations
												? "selected"
												: ""
										}`}
										onClick={() => {
											setShowAllConversations(false);
											setIsScopeMenuOpen(false);
										}}
									>
										<span className="agent-sidebar-scope-option-copy">
											<span className="agent-sidebar-scope-option-title">
												Self conversations
											</span>
											<span className="agent-sidebar-scope-option-subtitle">
												{currentUserName}
											</span>
										</span>
										<span className="agent-sidebar-scope-option-count">
											{ownerConversationCount}
										</span>
									</button>
									<button
										type="button"
										role="option"
										aria-selected={showAllConversations}
										className={`agent-sidebar-scope-menu-option ${
											showAllConversations ? "selected" : ""
										}`}
										onClick={() => {
											setShowAllConversations(true);
											setIsScopeMenuOpen(false);
										}}
									>
										<span className="agent-sidebar-scope-option-copy">
											<span className="agent-sidebar-scope-option-title">
												Everyone
											</span>
											<span className="agent-sidebar-scope-option-subtitle">
												All conversations
											</span>
										</span>
										<span className="agent-sidebar-scope-option-count">
											{allConversationCount}
										</span>
									</button>
								</div>
							)}
						</div>
					</div>
				)}
			</div>
			{!isCollapsed && isSearchVisible && (
				<SidebarSearch onSearch={handleSearch} />
			)}

			{isCollapsed && !readOnly && (
				<div className="agent-sidebar-new-chat">
					<button
						type="button"
						className="agent-sidebar-header-button"
						aria-label="New chat"
						title="New chat"
						onClick={handleNewChat}
					>
						<img
							src={newChatIcon}
							alt=""
							aria-hidden="true"
						/>
					</button>
				</div>
			)}

			{!isCollapsed &&
				(hasConversations ? (
					<div className="agent-sidebar-conversation-list">
						{Object.entries(groupedConversations).map(
							([group, convos]) =>
								convos.length > 0 && (
									<div
										key={group}
										className="conversation-group"
									>
										<h3 className="conversation-group-title">
											{group}
										</h3>
										<ul>
											{convos.map((convo) => {
												const isSelected =
													convo.conv_id ===
													selectedConversation?.conv_id;
												const isDeleting =
													deletingConversationId ===
													convo.conv_id;

												return (
													<li
														key={convo.conv_id}
														className="agent-sidebar-item"
													>
														<div
															className={`agent-sidebar-item-row ${
																isSelected ? "selected" : ""
															}`}
														>
															<Link
																to={toTenantPath(
																	`/agent/chat/${convo.conv_id}`,
																	activeTenant
																)}
																className="agent-sidebar-item-link"
																title={convo.title}
															>
																<UserAvatar
																	userName={convo.user_name}
																	size={24}
																	fontSize={11}
																/>
																<span className="agent-sidebar-item-title">
																	{convo.title}
																</span>
															</Link>
															{convo.is_owner && !readOnly && (
																<button
																	type="button"
																	className="agent-sidebar-delete-button"
																	aria-label={`Delete ${convo.title}`}
																	title="Delete conversation"
																	disabled={isDeleting}
																	onClick={(event) => {
																		handleOpenDeleteConversation(
																			event,
																			convo
																		);
																	}}
																>
																	<BinIcon stroke="currentColor" />
																</button>
															)}
														</div>
													</li>
												);
											})}
										</ul>
									</div>
								)
						)}
					</div>
				) : (
					<div className="no-conversations-message">
						<p>{emptyStateMessage}</p>
					</div>
				))}
			<DeleteConfirmationModal
				open={conversationPendingDeletion !== null}
				title="Delete Conversation"
				message={`Delete "${conversationPendingDeletion?.title ?? "this conversation"}"? This removes it from your history and cannot be undone.`}
				confirmText="Delete"
				loading={deletingConversationId !== null}
				className="agent-delete-conversation-modal"
				showCloseButton={true}
				handleClose={handleCloseDeleteConversation}
				confirmDelete={handleConfirmDeleteConversation}
			/>
		</div>
	);
};

export default Sidebar;
